/**
 * One drop of the list: drag handle (a pinned drop keeps its time, so it
 * does not drag), position, time + pin over its country time, per group
 * its chip over the exact list(s) this drop goes to (19/10/2026: see which
 * list on the hour, no sideways scroll), its gateway, amount (empty = the
 * default), on/off, remove.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Pin, X } from 'lucide-react';

import { Switch } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { GroupRef } from '../../hooks/use-general-times';
import type { SlotPatch } from '../../../state/slots/slot-ops';
import { DropLists } from './DropLists';
import { DropRoutePicker } from './DropRoutePicker';
import type { DayPlanGroupRow } from '@trafficpulse/shared';
import type { DayPlanDropRow, DraftGateway } from '../../../../../drop-generator.types';
import { DROP_GRID, type DropRowModel } from './drop-list-order';
import { DropTime } from './DropTime';
import type { CapMark } from '../cap/CapChip';

interface Props {
  row: DropRowModel;
  position: number;
  groupRefs: GroupRef[];
  defaultAmount: number;
  gateway: DraftGateway;
  countryKey?: string;
  planned: DayPlanDropRow[];
  routeTest?: boolean;
  planGroups: DayPlanGroupRow[];
  onPatch: (patch: SlotPatch) => void;
  onRemove: () => void;
  /** 20/10/2026 — what the daily SMS cap did to this drop today, and the clamped drop's amount. */
  cap?: { mark: CapMark; amount: number | null } | null;
}

export const DropRow: FC<Props> = ({ row, position, groupRefs, defaultAmount, gateway, countryKey, planned, planGroups, routeTest, onPatch, onRemove, cap = null }) => {
  const { slot, time, pinned } = row;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.key, disabled: pinned });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        `${DROP_GRID} border-b border-border/30 bg-background px-2 py-1 text-sm transition-colors hover:bg-muted/30`,
        isDragging && 'relative z-10 rounded-md shadow-lg ring-2 ring-primary/30',
        !slot.enabled && 'opacity-50',
      )}
    >
      {pinned ? (
        <span title="Pinned — it keeps its time" className="text-warning">
          <Pin className="h-3.5 w-3.5" />
        </span>
      ) : (
        <button
          type="button"
          aria-label={`Drag drop ${position}`}
          className="cursor-grab touch-none text-muted-foreground/60 hover:text-primary"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <span className="font-mono text-xs text-muted-foreground">{position}</span>
      <DropTime
        pinnedAtMin={slot.pinnedAtMin}
        time={time}
        enabled={slot.enabled}
        position={position}
        onPin={(pinnedAtMin) => onPatch({ pinnedAtMin })}
        sendOutside={slot.sendOutsideWindow === true}
        onSendOutside={(sendOutsideWindow) => onPatch({ sendOutsideWindow })}
        cap={cap?.mark ?? null}
        capAmount={cap?.amount ?? null}
      />
      <DropLists
        slot={slot}
        position={position}
        groupRefs={groupRefs}
        defaultAmount={defaultAmount}
        planned={planned}
        planGroups={planGroups}
        onPatch={onPatch}
      />
      <DropRoutePicker
        slot={slot}
        gateway={gateway}
        countryKey={countryKey}
        position={position}
        planned={planned[0]}
        routeTest={routeTest}
        onPatch={onPatch}
      />
      <input
        type="number"
        min={1}
        max={50000}
        value={slot.amount ?? ''}
        placeholder={String(defaultAmount)}
        onChange={(e) =>
          onPatch({
            amount: e.target.value === '' ? null : Number(e.target.value),
          })
        }
        className={`h-6 w-full rounded-md border bg-transparent px-2 font-mono text-xs tabular-nums outline-none ${slot.amount !== null ? 'border-primary/60' : 'border-input text-muted-foreground'}`}
        aria-label={`Drop ${position} amount`}
      />
      <Switch checked={slot.enabled} onCheckedChange={(enabled) => onPatch({ enabled })} aria-label={`Drop ${position} on`} />
      <button
        type="button"
        aria-label={`Remove drop ${position}`}
        onClick={onRemove}
        className="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};
